import { setDefaultVolume, setDefaultMutedState } from './settings';

// The event list that follows needs to be the same for all platforms

const trackingEvents = {
  EVENT_READY: 'READY',
  EVENT_STARTED: 'START',
  EVENT_ACTIVE_VIEW: 'ACTIVE_VIEW',
  EVENT_MUTED: 'MUTE',
  EVENT_UNMUTED: 'UNMUTE',
  EVENT_FULLSCREEN_STARTED: 'FULLSCREEN_START',
  EVENT_FULLSCREEN_EXITED: 'FULLSCREEN_EXIT',
  EVENT_WATCHED_3_SECONDS: 'THREE_SECONDS',
  EVENT_WATCHED_10_SECONDS: 'TEN_SECONDS',
  EVENT_WATCHED_25_PERCENT: 'TWENTY_FIVE_PERCENT',
  EVENT_WATCHED_50_PERCENT: 'FIFTY_PERCENT',
  EVENT_WATCHED_75_PERCENT: 'SEVENTY_FIVE_PERCENT',
  EVENT_WATCHED_95_PERCENT: 'NINETY_FIVE_PERCENT',
  EVENT_WATCHED_100_PERCENT: 'ONE_HUNDRED_PERCENT',
  EVENT_SETUP_ERROR: 'SETUP_ERROR',
  EVENT_PLAYER_ERROR: 'PLAYER_ERROR',
};

const nonTrackingEvents = {
  EVENT_PLAY: 'play',
  EVENT_PAUSE: 'pause',
};

const events = {
  ...trackingEvents,
  ...nonTrackingEvents,
};

// key is the second watched; value is xing event name
const secondsWatchedEventList: Record<number, string> = {
  3: events.EVENT_WATCHED_3_SECONDS,
  10: events.EVENT_WATCHED_10_SECONDS,
};

// key is the percentage watched; value is xing event name
const percentageWatchedEventList: Record<number, string> = {
  25: events.EVENT_WATCHED_25_PERCENT,
  50: events.EVENT_WATCHED_50_PERCENT,
  75: events.EVENT_WATCHED_75_PERCENT,
  95: events.EVENT_WATCHED_95_PERCENT,
};

interface EventsHandler {
  onReady?: () => void;
  onStart?: () => void;
  onMute?: (muted: boolean) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onError?: () => void;
}

type EventHandlerProps = {
  xingPlayer: XingPlayer;
  section: string;
  extraFields?: object;
  eventsHandler: EventsHandler;
  trackEventMutation: (options: any) => void;
};
export class EventHandler {
  private xingPlayer: XingPlayer;
  private section: string;
  private extraFields?: object;
  private eventsHandler: EventsHandler;
  private trackEventMutation: (options: any) => void;
  private inViewTimer: any;
  private triggerPercentageEventsList: Record<number, string>;
  private triggerSecondsWatchedEventsList: Record<number, string>;
  private lastSecondChecked: number | null;

  constructor({
    xingPlayer,
    section,
    extraFields,
    eventsHandler,
    trackEventMutation,
  }: EventHandlerProps) {
    this.xingPlayer = xingPlayer;
    this.eventsHandler = eventsHandler;
    this.trackEventMutation = trackEventMutation;
    this.section = section;
    this.extraFields = extraFields;
    this.inViewTimer = null;
    this.triggerPercentageEventsList = {};
    this.triggerSecondsWatchedEventsList = {};
    this.lastSecondChecked = null;
    this.init();
  }

  private init(): void {
    // trigger ready event
    this.triggerEvent(trackingEvents.EVENT_READY);

    // init event handler
    this.xingPlayer.videojs.on('firstplay', () => this.firstplayHandler());
    this.xingPlayer.videojs.on('play', () => this.playHandler());
    this.xingPlayer.videojs.on('pause', () => this.pauseHandler());
    this.xingPlayer.videojs.on('timeupdate', () => this.timeUpdateHandler());
    this.xingPlayer.videojs.on('durationchange', () =>
      this.durationChangeHandler()
    );
    this.xingPlayer.videojs.on('volumechange', () => {
      this.volumeChangeHandler();
    });
    this.xingPlayer.videojs.on('fullscreenchange', () =>
      this.fullscreenChangeHandler()
    );
    this.xingPlayer.videojs.on('ended', () => this.endedHandler());
  }

  private triggerEvent(event: string): void {
    this.triggerTrackingEvent(event);
    this.triggerXingVideoEvent(event);
  }

  private triggerTrackingEvent(event: string): void {
    const data = {
      collection: [
        {
          type: event,
          siteSection: this.section,
          clientTimestamp: new Date(),
          videoRef: this.xingPlayer.videoId,
          extraFields: JSON.stringify(this.extraFields ?? {}),
        },
      ],
    };

    this.trackEventMutation({ variables: { input: data } });
  }

  private triggerXingVideoEvent(event: string): void {
    switch (event) {
      case events.EVENT_READY:
        this.eventsHandler.onReady?.();
        break;
      case events.EVENT_STARTED:
        this.eventsHandler.onStart?.();
        break;
      case events.EVENT_MUTED:
        this.eventsHandler.onMute?.(true);
        break;
      case events.EVENT_UNMUTED:
        this.eventsHandler.onMute?.(false);
        break;
      case events.EVENT_PLAY:
        this.eventsHandler.onPlay?.();
        break;
      case events.EVENT_PAUSE:
        this.eventsHandler.onPause?.();
        break;
      case events.EVENT_WATCHED_100_PERCENT:
        this.eventsHandler.onComplete?.();
        break;
      case events.EVENT_SETUP_ERROR || events.EVENT_PLAYER_ERROR:
        this.eventsHandler.onError?.();
        break;
    }
  }

  // Events Handler Functions
  private firstplayHandler(): void {
    this.triggerEvent(trackingEvents.EVENT_STARTED);
    this.checkVideoActivity();
  }

  private playAgainHandler(): void {
    this.triggerEvent(trackingEvents.EVENT_STARTED);
    this.xingPlayer.activeTrackingSent = false;
    this.xingPlayer.ended = false;
  }

  private playHandler(): void {
    if (this.xingPlayer.ended) {
      this.playAgainHandler();
    }
    this.checkVideoActivity();
    if (
      !this.xingPlayer.videojs.seeking() &&
      !this.xingPlayer.videojs.ended()
    ) {
      this.triggerXingVideoEvent(events.EVENT_PLAY);
    }
    this.playToogleHandler();
  }

  private pauseHandler(): void {
    if (
      !this.xingPlayer.videojs.seeking() &&
      !this.xingPlayer.videojs.ended()
    ) {
      this.triggerXingVideoEvent(events.EVENT_PAUSE);
    }
    this.playToogleHandler();
  }

  private playToogleHandler(): void {
    const bigPlayButton = this.xingPlayer.videojs
      .el()
      .querySelector('.vjs-big-play-button');

    bigPlayButton?.classList?.add?.('xing-vjs-changing-status');

    setTimeout(
      () => bigPlayButton?.classList?.remove?.('xing-vjs-changing-status'),
      500
    );
  }

  private checkVideoActivity(): void {
    if (
      !this.xingPlayer.activeTrackingSent &&
      this.xingPlayer.inView &&
      !this.xingPlayer.videojs.muted() &&
      !this.xingPlayer.videojs.paused()
    ) {
      this.triggerEvent(trackingEvents.EVENT_ACTIVE_VIEW);
      this.xingPlayer.activeTrackingSent = true;
    }
  }

  private volumeChangeHandler(): void {
    const previousMuteState = this.xingPlayer.isMuted;
    const muted = this.xingPlayer.videojs.muted();
    const volume = this.xingPlayer.videojs.volume();

    // save user volume preferences
    setDefaultVolume(volume.toString());

    // videojs does not have a mute event so we need to implement it with volumechange
    if (previousMuteState !== muted) {
      this.xingPlayer.isMuted = muted;
      this.triggerEvent(
        muted ? trackingEvents.EVENT_MUTED : trackingEvents.EVENT_UNMUTED
      );

      // save user muted preferences
      setDefaultMutedState(muted);

      if (!muted) {
        this.checkVideoActivity();
      }
    }
  }

  handleIntersection(): void {
    if (this.xingPlayer.inView) {
      this.checkVideoActivity();
    }
  }

  private fullscreenChangeHandler(): void {
    this.triggerEvent(
      this.xingPlayer.videojs.isFullscreen()
        ? trackingEvents.EVENT_FULLSCREEN_STARTED
        : trackingEvents.EVENT_FULLSCREEN_EXITED
    );
  }

  private timeUpdateHandler(): void {
    const time = Math.floor(this.xingPlayer.videojs.currentTime());

    if (
      this.lastSecondChecked !== time &&
      this.triggerPercentageEventsList[time]
    ) {
      this.triggerEvent(this.triggerPercentageEventsList[time]);
    }
    if (
      this.lastSecondChecked !== time &&
      this.triggerSecondsWatchedEventsList[time]
    ) {
      this.triggerEvent(this.triggerSecondsWatchedEventsList[time]);
    }

    this.lastSecondChecked = time;
  }

  private durationChangeHandler(): void {
    const videoDuration = this.xingPlayer.videojs.duration();

    this.triggerSecondsWatchedEventsList = { ...secondsWatchedEventList };

    Object.entries(percentageWatchedEventList).map(([key, value]) => {
      this.triggerPercentageEventsList[
        Math.floor((videoDuration * parseInt(key)) / 100)
      ] = value;
    });
  }

  private endedHandler(): void {
    this.xingPlayer.ended = true;
    this.triggerEvent(trackingEvents.EVENT_WATCHED_100_PERCENT);
  }
}
